import React from 'react'
import {
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  styled,
  Divider,
  Chip,
  FormControlLabel,
  Checkbox,
} from '@mui/material'
import LocalHospital from '@mui/icons-material/LocalHospital'
import Psychology from '@mui/icons-material/Psychology'
import Notes from '@mui/icons-material/Notes'
import Event from '@mui/icons-material/Event'
import Attachment from '@mui/icons-material/Attachment'
import DiagnosisSelectMenu from 'src/mentor/components/PrescriptionMakingPage/inputComponents/diagnosis/DiagnosisMenu'
import { SuggestedTherapySelectMenu } from 'src/mentor/components/PrescriptionMakingPage/PrescriptionMakingPageComponents'
import EMRNextVisitComponent from 'src/mentor/components/PrescriptionMakingPage/screeningReportViewer/components/prescriptionMakingPageComponents/EMRNextVisitComponent'

const ModernCard = styled(Card)(({ theme }) => ({
  borderRadius: '12px',
  border: '1px solid rgba(67, 56, 202, 0.15)',
  background: 'linear-gradient(135deg, rgba(67, 56, 202, 0.02) 0%, rgba(99, 102, 241, 0.02) 100%)',
  marginBottom: '16px',
  transition: 'all 0.2s ease',
  '&:hover': {
    borderColor: 'rgba(67, 56, 202, 0.3)',
    boxShadow: '0 4px 12px rgba(67, 56, 202, 0.08)',
  },
}))

const SectionHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  marginBottom: '12px',
}))

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontSize: '14px',
  fontWeight: 600,
  color: '#4338ca',
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
}))

// Fix component to render as div to avoid invalid DOM nesting
SectionTitle.defaultProps = {
  component: 'div',
}

const HelperText = styled(Typography)(({ theme }) => ({
  fontSize: '12px',
  color: 'rgba(0, 0, 0, 0.6)',
  marginTop: '4px',
  fontStyle: 'italic',
}))

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: '8px',
    background: '#fff',
    '&:hover fieldset': {
      borderColor: 'rgba(67, 56, 202, 0.5)',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#4338ca',
    },
  },
}))

const RequiredBadge = styled(Chip)(({ theme }) => ({
  height: '20px',
  fontSize: '10px',
  fontWeight: 600,
  background: 'rgba(239, 68, 68, 0.1)',
  color: '#dc2626',
  border: '1px solid rgba(239, 68, 68, 0.2)',
}))

export default function ModernSendTherapyComponent({
  clinicId,
  clinicBrandId,
  selectedDiagnosis,
  setSelectedDiagnosis,
  nextVisitInputValue,
  setNextVisitInputValue,
  nextVisitInputType,
  setNextVisitInputType,
  bookedEventInfo,
  loading,
  notesToTherapist,
  setNotesToTherapist,
  selectedTherapies,
  setSelectedTherapies,
  attachPrescriptionForTherapy,
  setAttachPrescriptionForTherapy,
}) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {/* Diagnosis Section - Shared across both tabs */}
      <ModernCard>
        <CardContent>
          <SectionHeader>
            <SectionTitle>
              <LocalHospital sx={{ fontSize: 18 }} />
              Diagnosis
              <RequiredBadge label="Required" size="small" />
            </SectionTitle>
          </SectionHeader>
          <HelperText>
            Select the primary diagnosis and any additional conditions (shared with Assessment Referral)
          </HelperText>
          <Box sx={{ marginTop: 2 }}>
            <DiagnosisSelectMenu
              clinicId={clinicId}
              clinicBrandId={clinicBrandId}
              selectedDiagnosis={selectedDiagnosis}
              setSelectedDiagnosis={setSelectedDiagnosis}
              menuPortalTarget={document.body}
              maxZIndex
              isLoading={loading}
            />
          </Box>
        </CardContent>
      </ModernCard>

      {/* Suggested Therapies Section */}
      <ModernCard>
        <CardContent>
          <SectionHeader>
            <SectionTitle>
              <Psychology sx={{ fontSize: 18 }} />
              Suggested Therapies
            </SectionTitle>
          </SectionHeader>
          <HelperText>
            Select the recommended therapy types for this patient
          </HelperText>
          <Box sx={{ marginTop: 2 }}>
            <SuggestedTherapySelectMenu
              selectedTherapies={selectedTherapies}
              setSelectedTherapies={setSelectedTherapies}
              menuPortalTarget={document.body}
              maxZIndex
            />
          </Box>
        </CardContent>
      </ModernCard>

      {/* Notes Section */}
      <ModernCard>
        <CardContent>
          <SectionHeader>
            <SectionTitle>
              <Notes sx={{ fontSize: 18 }} />
              Notes to Psychologist
            </SectionTitle>
          </SectionHeader>
          <HelperText>
            Provide any additional context or instructions for the therapist
          </HelperText>
          <StyledTextField
            fullWidth
            multiline
            rows={4}
            placeholder="Enter notes that you want to send to the psychologists who will take this case..."
            value={notesToTherapist || ''}
            onChange={(e) => setNotesToTherapist(e.target.value)}
            sx={{ marginTop: 2 }}
          />
        </CardContent>
      </ModernCard>

      {/* Next Visit Section */}
      <ModernCard>
        <CardContent>
          <SectionHeader>
            <SectionTitle>
              <Event sx={{ fontSize: 18 }} />
              Next Visit
            </SectionTitle>
          </SectionHeader>
          <HelperText>
            Schedule the follow-up appointment for this patient
          </HelperText>
          <Box sx={{ marginTop: 2 }}>
            <EMRNextVisitComponent
              nextVisitInputValue={nextVisitInputValue}
              setNextVisitInputValue={setNextVisitInputValue}
              nextVisitInputType={nextVisitInputType}
              setNextVisitInputType={setNextVisitInputType}
              bookedEventInfoStartTime={bookedEventInfo?.startTime}
              currentEventTypeId={bookedEventInfo?.eventTypeId}
              hideHoldSlotForNextVisit
              currentPatient={bookedEventInfo?.patient}
              currentFee={bookedEventInfo?.fee}
              currentBookedEventId={bookedEventInfo?.id}
              nextVisitBookedEventId={bookedEventInfo?.nextVisitReferenceBookedEventId}
            />
          </Box>
        </CardContent>
      </ModernCard>

      {/* Attach Prescription Checkbox */}
      <ModernCard>
        <CardContent>
          <FormControlLabel
            control={
              <Checkbox
                checked={attachPrescriptionForTherapy || false}
                onChange={(e) => setAttachPrescriptionForTherapy(e.target.checked)}
                sx={{
                  color: '#4338ca',
                  '&.Mui-checked': {
                    color: '#4338ca',
                  },
                }}
              />
            }
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Attachment sx={{ fontSize: 18, color: '#4338ca' }} />
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#4338ca' }}>
                    Attach Prescription/Report
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'rgba(0, 0, 0, 0.6)' }}>
                    Send the prescription report to the therapist who receives this referral
                  </Typography>
                </Box>
              </Box>
            }
          />
        </CardContent>
      </ModernCard>
    </Box>
  )
}

